"use client";

import { memo, useEffect, useId, useMemo } from "react";

// When factset has a new updates to their widgets we can download from https://rwbaird-widgets.services.production.gateway.factset.com/download/widgets-wc.js amd add the script to the assets folder
import { logCustomError } from "#app/lib/logger";

export type ConfigItem =
  | { type: "attribute"; key: string; value: unknown }
  | { type: "event"; key: string; value: (event: any) => void };

type FactSetWidgetProps = {
  config: ConfigItem[];
  widget: FactSetElements;
  className?: string;
};

type FactSetStoreEvent = {
  detail: Error[];
};

const hintAttributes = new Set(["criteria", "identifiers", "identifier"]);

const FactSetWidget = memo(
  ({ config, widget, className }: FactSetWidgetProps) => {
    const widgetId = useId();

    const widgetElement = useMemo(() => {
      const attributes = config.filter((el) => el.type === "attribute");
      const events = config.filter((el) => el.type === "event");
      const element = document.createElement(`fds-${widget}`) as any;
      element.classList.add("fds");
      const classList = className?.split(" ") ?? [];
      element.classList.add(...classList);

      if (element) {
        if (attributes.length > 0) {
          for (const attr of attributes) {
            element[attr.key] = attr.value;
          }
        }
        if (events.length > 0) {
          for (const ev of events) {
            element.addEventListener(ev.key, ev.value);
          }
        }
        element["locale"] = "en-US";
        element.addEventListener(
          "store-fatal",
          ({ detail }: FactSetStoreEvent) => {
            const hint = config
              .filter((el) => hintAttributes.has(el.key))
              .map((el) => JSON.stringify(el.value))
              .join(",");
            logCustomError(
              `FactSetWidget:Fatal:${widget}`,
              detail[0]?.message ?? "Unknown Error",
              hint,
            );
          },
        );
        element.addEventListener(
          "store-error",
          ({ detail }: FactSetStoreEvent) => {
            const hint = config
              .filter((el) => hintAttributes.has(el.key))
              .map((el) => JSON.stringify(el.value))
              .join(",");
            logCustomError(
              `FactSetWidget:Error:${widget}`,
              detail[0]?.message ?? "Unknown Error",
              hint,
            );
          },
        );
      }
      return element;
    }, [config, widget, className]);

    useEffect(() => {
      document.getElementById(widgetId)?.appendChild(widgetElement);
      return () => {
        document.getElementById(widgetId)?.removeChild(widgetElement);
      };
    }, [widgetElement, widgetId]);

    return <div id={widgetId} />;
  },
);

FactSetWidget.displayName = "FactSetWidget";

export { FactSetWidget };
